import * as XLSX from "xlsx";
import moment from "moment-timezone";
import { displayElapsed } from "./finishTime";

/**
 * Drop the fractional seconds from a displayed duration.
 *
 * Times are stored to the millisecond because ranking needs that precision —
 * the 6K silver and bronze are 3ms apart — but a results sheet is read by
 * runners and officials, where "01:02:33.998" is noise. The results pages
 * already strip it; the export was writing the raw value through.
 *
 * Ordering is unaffected: rows are sorted on overallRank before this runs.
 */
const stripMs = (v) => (typeof v === "string" ? v.replace(/\.\d+$/, "") : v);

// Helper: Convert time to seconds
const timeToSeconds = (time) => {
    if (!time) return Infinity;

    // Handle COT format
    if (time.includes("COT")) {
        const parts = time.split("/");
        if (parts.length === 2) {
            const duration = parts[1].trim();
            const [h, m, s] = duration.split(":").map(Number);
            if (!isNaN(h) && !isNaN(m) && !isNaN(s)) {
                return h * 3600 + m * 60 + s;
            }
        }
        const cotMatch = time.match(/COT \((\d{2}:\d{2}:\d{2})\)/);
        if (cotMatch) {
            const [h, m, s] = cotMatch[1].split(":").map(Number);
            return h * 3600 + m * 60 + s;
        }
        return Infinity;
    }

    const [h, m, s] = time.split(":").map(Number);
    if (isNaN(h) || isNaN(m) || isNaN(s)) return Infinity;
    return h * 3600 + m * 60 + s;
};

/**
 * Generates and downloads an Excel file from race results.
 * @param {Array} results - API results array
 * @param {Array} categories - Event categories definition
 * @param {string} eventId - ID of the event for filename
 */
// Helper to safely parse any timestamp for export
const parseExportTimestamp = (ts) => {
    if (!ts) return null;
    if (moment.isMoment(ts)) return ts;
    if (ts instanceof Date) return moment(ts);
    if (typeof ts === 'string') return moment(ts);
    if (ts.toDate && typeof ts.toDate === 'function') return moment(ts.toDate());
    if (ts._seconds !== undefined || ts.seconds !== undefined) {
        const seconds = ts._seconds || ts.seconds;
        const nanoseconds = ts._nanoseconds || ts.nanoseconds || 0;
        return moment(seconds * 1000 + nanoseconds / 1000000);
    }
    return moment(ts);
};

export const generateAndDownloadExcel = (results, categories, eventId) => {
    if (!results || results.length === 0) {
        throw new Error("No results to export");
    }

    const wb = XLSX.utils.book_new();

    // Group by Category
    const grouped = {};
    results.forEach((r) => {
        const cat = r.categoryId || "Uncategorized";
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(r);
    });

    // Metadata Map
    const categoryMeta = categories.reduce((acc, cat) => {
        acc[cat.id] = cat;
        return acc;
    }, {});

    // Process Groups
    Object.keys(grouped).forEach((catId) => {
        let runners = grouped[catId];
        const category = categoryMeta[catId];
        const isLooping = category?.isLooping || false;

        // --- SORTING LOGIC ---
        if (isLooping) {
            // Sort by Completed Loops (DESC), then Last Loop Time (ASC)
            runners.sort((a, b) => {
                // Same rule as the standard path: trust the backend's placing
                // first so the sheet matches the loop results page, and only
                // fall back to local ordering for unranked rows.
                const ar = a.overallRank > 0 ? a.overallRank : Infinity;
                const br = b.overallRank > 0 ? b.overallRank : Infinity;
                if (ar !== br) return ar - br;

                const loopsA = a.completedLoops || 0;
                const loopsB = b.completedLoops || 0;
                if (loopsA !== loopsB) return loopsB - loopsA;

                // Same loops: compare time of last loop
                let timeA = Infinity;
                let timeB = Infinity;

                const getLastMs = (runner, loopNum) => {
                    if (loopNum <= 0) return Infinity;
                    const key = `loop${loopNum}`;
                    if (runner.checkpoints) {
                        // Checkpoints structure: { cpId: { loop1: { time: ... } } }
                        // Or checks `loopX` directly inside cpData?
                        // cpData structure seen elsewhere: cpData[`loop${i}`].time
                        for (const cp of Object.values(runner.checkpoints)) {
                            if (cp[key] && cp[key].time) {
                                const m = parseExportTimestamp(cp[key].time);
                                if (m && m.isValid()) return m.valueOf();
                            }
                        }
                    }
                    return Infinity;
                };

                if (loopsA > 0) timeA = getLastMs(a, loopsA);
                if (loopsB > 0) timeB = getLastMs(b, loopsB);

                return timeA - timeB;
            });
        } else {
            // Standard Sorting (Status then Time)
            // COT Calculation
            let cotLimitSeconds = Infinity;
            if (category && category.startTime && category.cutOffTime) {
                const start = moment(category.startTime);
                const end = moment(category.cutOffTime);
                if (start.isValid() && end.isValid()) {
                    cotLimitSeconds = end.diff(start, "seconds");
                }
            }

            // Enrich Status & Time
            runners = runners.map((r) => {
                // ... (Existing standard logic) ...
                // Re-implementing simplified inline for consistency
                if (r.status && ["DNF", "DNS", "DSQ"].includes(r.status)) {
                    return { ...r, _sortStatus: 2, _seconds: Infinity, _displayTime: r.status };
                }

                const finishCheckpointTime = r.checkpoints?.finish?.time;
                let isValidFinish = false;
                let seconds = Infinity;
                let displayTime = "DNF";

                // Buzzer-timed events (ninja/obstacle, speed climbing) store an
                // elapsed time and no mat readings, so the checkpoint path below
                // finds nothing and would export a finisher as "DNF".
                if (!finishCheckpointTime && r.finishDuration) {
                    const parts = String(r.finishDuration).split(":");
                    const secs = parts.length === 3
                        ? Number(parts[0]) * 3600 + Number(parts[1]) * 60 + parseFloat(parts[2])
                        : NaN;
                    if (Number.isFinite(secs)) {
                        return {
                            ...r,
                            _sortStatus: 0,
                            _seconds: secs,
                            // Kept raw: the precision shown is decided once, in
                            // the column mapping below, from the category.
                            _displayTime: String(r.finishDuration),
                        };
                    }
                }

                if (finishCheckpointTime && category?.startTime) {
                    const start = moment(category.startTime);
                    const finish = parseExportTimestamp(finishCheckpointTime);
                    if (start.isValid() && finish.isValid()) {
                        isValidFinish = true;
                        seconds = finish.diff(start, "seconds");
                        // Use stored finishTime for display if available
                        if (r.finishTime) {
                            displayTime = r.finishTime;
                        } else {
                            // Fallback format
                            const d = moment.duration(seconds, "seconds");
                            displayTime = [d.hours(), d.minutes(), d.seconds()]
                                .map(v => Math.floor(v).toString().padStart(2, '0')).join(':');
                        }
                    }
                }

                let isCOT = false;
                if (isValidFinish && seconds > cotLimitSeconds) isCOT = true;
                let sortStatus = 2;
                if (isValidFinish) sortStatus = isCOT ? 1 : 0;
                if (sortStatus === 2) displayTime = "DNF";

                return { ...r, _sortStatus: sortStatus, _seconds: seconds, _isCOT: isCOT, _displayTime: displayTime };
            });

            // Rank exactly as the results pages do — by the backend's
            // overallRank. Re-deriving the order here drifted from what the
            // public standings show:
            //   • penaltySeconds was ignored, so BIB 1300 (a 90-minute penalty)
            //     exported at rank 3 while the site had her far down the field;
            //   • diff(…, "seconds") truncated milliseconds, so runners in the
            //     same second fell back to array order — the 6K silver and
            //     bronze are 3ms apart;
            //   • only checkpoints.finish counted, missing finishMatTime.
            // Unranked rows (overallRank -1: COT, DNF/DNS/DSQ, no finish) keep
            // the local status/time ordering and sit below every ranked runner.
            runners.sort((a, b) => {
                const ar = a.overallRank > 0 ? a.overallRank : Infinity;
                const br = b.overallRank > 0 ? b.overallRank : Infinity;
                if (ar !== br) return ar - br;
                if (a._sortStatus !== b._sortStatus) return a._sortStatus - b._sortStatus;
                return a._seconds - b._seconds;
            });
        }


        // --- MAP TO ROWS ---
        const rows = runners.map((r, index) => {
            const baseRow = {
                // The backend's placing, not the row number: index + 1 handed a
                // rank to COT and DNF/DNS/DSQ rows too, so the sheet showed
                // placings the results page never awarded.
                Rank: r.overallRank > 0 ? r.overallRank : "",
                BIB: r.bib,
                Name: r.name,
                Gender: r.gender,
                Age: r.age ?? "",
                // Age is derived and goes stale every birthday; the date is what
                // an age-category protest is actually settled on.
                "Birth Date": r.birthDate ?? "",
                Nationality: r.nationality ?? "",
                "Category ID": r.categoryId ?? "",
                // `race` is the operator-facing coarse label (e.g. "42K", "10K")
                // stored verbatim from the participant CSV & echoed back onto the
                // result record during import / MYLAPS ingest. Persisted here so
                // downstream reports can group cross-category.
                Race: r.race ?? "",
                // MYLAPS transponder code — handy for post-race reconciliation
                // against the T&S plug-in logs.
                "Chip Code": r.chipCode ?? "",
            };

            if (isLooping) {
                // Loop columns extend to the highest loop actually recorded in
                // this category. There is no configured target or ceiling.
                const maxLoops = runners.reduce((mx, r) => {
                    const cps = r.checkpoints || {};
                    return Object.values(cps).reduce((m, c) => {
                        if (!c || typeof c !== 'object') return m;
                        return Object.keys(c).reduce((n, k) => {
                            const v = /^loop(\d+)$/.test(k) ? parseInt(k.slice(4), 10) : 0;
                            return v > n ? v : n;
                        }, m);
                    }, mx);
                }, 0);
                const runnerLastLoop = Object.values(r.checkpoints || {}).reduce((mx, cp) => {
                    if (!cp || typeof cp !== 'object') return mx;
                    return Object.keys(cp).reduce((m, key) => {
                        const n = /^loop(\d+)$/.test(key) && cp[key]?.time ? parseInt(key.slice(4), 10) : 0;
                        return Math.max(m, n);
                    }, mx);
                }, 0);
                const loopData = {};
                // Pre-calculate duration for finished runners to use in Values column
                let calculatedDuration = null;
                if (runnerLastLoop > 0 && category.startTime) {
                    let lastLoopTs = null;
                    const lastLoopKey = `loop${runnerLastLoop}`;
                    if (r.checkpoints) {
                        Object.values(r.checkpoints).forEach(cpData => {
                            if (cpData[lastLoopKey]) {
                                const t = cpData[lastLoopKey].time;
                                const m = parseExportTimestamp(t);
                                if (m && m.isValid()) lastLoopTs = m;
                            }
                        });
                    }
                    const startTs = moment(category.startTime);
                    if (lastLoopTs && startTs.isValid()) {
                        const diffMs = lastLoopTs.diff(startTs);
                        // Format manually to handle > 24 hours
                        const duration = moment.duration(diffMs);
                        const hours = Math.floor(duration.asHours());
                        const mins = duration.minutes();
                        const secs = duration.seconds();
                        // Pad with zeros
                        calculatedDuration = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                    }
                }

                for (let i = 1; i <= maxLoops; i++) {
                    let timeStr = "-";
                    const isLastLoop = i === runnerLastLoop;

                    // Get Clock Time first
                    let clockTimeStr = "";
                    if (r.checkpoints) {
                        Object.values(r.checkpoints).forEach(cpData => {
                            if (cpData[`loop${i}`]) {
                                const t = cpData[`loop${i}`].time;
                                const m = parseExportTimestamp(t);
                                if (m && m.isValid()) {
                                    clockTimeStr = m.tz("Asia/Jakarta").format("HH:mm:ss");
                                }
                            }
                        });
                    }

                    // Formatting Logic
                    if (isLastLoop && calculatedDuration && clockTimeStr) {
                        // User Request: L7 = "Clock (Duration)"
                        timeStr = `${clockTimeStr} (${calculatedDuration})`;
                    } else if (clockTimeStr) {
                        timeStr = clockTimeStr;
                    }

                    loopData[`L${i}`] = timeStr;
                }



                // Finish Time / Status for Loop logic
                let finishDisplay = "-";

                // Determine Values column
                if (calculatedDuration) {
                    finishDisplay = calculatedDuration;
                } else if (r.finishTime && r.finishTime !== "-") {
                    // Fallback to stored finishTime if calculation failed
                    if (typeof r.finishTime === 'string') {
                        finishDisplay = r.finishTime;
                    }
                }

                // Fallback for ongoing runners
                if ((finishDisplay === "-" || !finishDisplay) && r.completedLoops > 0) {
                    const key = `L${r.completedLoops}`;
                    if (loopData[key] && loopData[key] !== "-") {
                        finishDisplay = `Loop ${r.completedLoops}`;
                    } else {
                        finishDisplay = `Loop ${r.completedLoops}`;
                    }
                }

                if (finishDisplay === "-" && r.completedLoops > 0) {
                    finishDisplay = `Loop ${r.completedLoops}`;
                }

                return {
                    ...baseRow,
                    ...loopData,
                    "Values": stripMs(finishDisplay) // Finish column
                };

            } else {
                // Standard Columns
                let finishTimeDisplay = displayElapsed(r._displayTime, category);
                if (r._isCOT) finishTimeDisplay = `COT (${displayElapsed(r._displayTime, category)})`;
                return {
                    ...baseRow,
                    // Wall-clock official time (gun). For mass-start events this equals the
                    // duration from category.startTime. Sourced from MYLAPS `gunTime` field
                    // or computed when only a finish timestamp is available.
                    "Gun Time": stripMs(r.gunTime) || finishTimeDisplay,
                    // Net / chip time (start mat → finish mat). Only meaningful when the
                    // event has a start mat or individual start. Empty for mass-start only.
                    "Net Time": stripMs(r.netTime) || "",
                    "Finish Time": finishTimeDisplay,
                    Status: r.status || (r._isCOT ? "COT" : ""),
                };
            }
        });

        // Sheet Name
        let sheetName = category?.name || catId;
        sheetName = sheetName.replace(/[\/\\\?\*\[\]]/g, "_").substring(0, 31);
        if (wb.SheetNames.includes(sheetName)) {
            sheetName = `${sheetName}_${catId}`.substring(0, 31);
        }

        const ws = XLSX.utils.json_to_sheet(rows);

        // Columns Width — order mirrors `baseRow` keys above.
        const cols = [
            { wch: 6 },  // Rank
            { wch: 10 }, // BIB
            { wch: 30 }, // Name
            { wch: 10 }, // Gender
            { wch: 6 },  // Age
            { wch: 12 }, // Birth Date
            { wch: 15 }, // Nationality
            { wch: 15 }, // Category ID
            { wch: 10 }, // Race
            { wch: 12 }, // Chip Code
        ];

        if (isLooping) {
            // Add dynamic width for loops (same rule as the row builder above)
            const maxLoops = runners.reduce((mx, r) => {
                const cps = r.checkpoints || {};
                return Object.values(cps).reduce((m, c) => {
                    if (!c || typeof c !== 'object') return m;
                    return Object.keys(c).reduce((n, k) => {
                        const v = /^loop(\d+)$/.test(k) ? parseInt(k.slice(4), 10) : 0;
                        return v > n ? v : n;
                    }, m);
                }, mx);
            }, 0);
            for (let i = 0; i < maxLoops; i++) cols.push({ wch: 10 }); // L1, L2...
            cols.push({ wch: 20 }); // Values/Finish
        } else {
            cols.push({ wch: 12 }); // Gun Time
            cols.push({ wch: 12 }); // Net Time
            cols.push({ wch: 20 }); // Finish Time
            cols.push({ wch: 10 }); // Status
        }

        ws["!cols"] = cols;

        XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    // ─────────────────────────────────────────────────────────────────────
    // `_raw` backup sheet — one row per checkpoint per runner. Columns mirror
    // the POST /api/timing/import-results payload exactly so this very file
    // can be re-imported to restore state after a Firestore wipe. Hidden from
    // the operator by default (but still visible in Excel's sheet tab).
    // ─────────────────────────────────────────────────────────────────────
    const rawRows = [];
    results.forEach((r) => {
        const cps = r.checkpoints || {};
        Object.entries(cps).forEach(([locationId, cpData]) => {
            if (!cpData) return;
            // `cpData` can either be a flat { time, location } shape (standard
            // races) or a nested { loop1: {...}, loop2: {...}, ... } shape
            // (looping races). Emit one row per leaf timestamp so both forms
            // round-trip losslessly.
            const emit = (loc, time) => {
                if (!time) return;
                const iso = (() => {
                    const m = parseExportTimestamp(time);
                    return m && m.isValid() ? m.toISOString() : (typeof time === "string" ? time : "");
                })();
                rawRows.push({
                    bib: r.bib ?? "",
                    chipCode: r.chipCode ?? "",
                    race: r.race ?? r.categoryId ?? "",
                    categoryId: r.categoryId ?? "",
                    location: loc,
                    finishTime: iso,
                    gunTime: r.gunTime ?? "",
                    netTime: r.netTime ?? "",
                    name: r.name ?? "",
                    gender: r.gender ?? "",
                    age: r.age ?? "",
                    nationality: r.nationality ?? "",
                });
            };

            if (cpData.time !== undefined) {
                emit(locationId, cpData.time);
            } else {
                // Looping shape: iterate loop1..loopN.
                Object.entries(cpData).forEach(([loopKey, loopVal]) => {
                    if (loopVal && loopVal.time) emit(loopKey, loopVal.time);
                });
            }
        });
    });

    if (rawRows.length > 0) {
        const wsRaw = XLSX.utils.json_to_sheet(rawRows);
        wsRaw["!cols"] = [
            { wch: 8 },  // bib
            { wch: 12 }, // chipCode
            { wch: 8 },  // race
            { wch: 10 }, // categoryId
            { wch: 12 }, // location
            { wch: 26 }, // finishTime (ISO)
            { wch: 10 }, // gunTime
            { wch: 10 }, // netTime
            { wch: 28 }, // name
            { wch: 8 },  // gender
            { wch: 6 },  // age
            { wch: 14 }, // nationality
        ];
        XLSX.utils.book_append_sheet(wb, wsRaw, "_raw");
    }

    // Write File safely
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${eventId}_results.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
